// @flow

import React, { useState } from 'react';
import { get, partial } from 'lodash';

import {
  type ApiKeys,
  type CreditCardBrandCode,
  type PaymentDetailsCard,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { Button, Text } from '@zapier/design-system';
import CreditCardForm from '@zapier/payment/src/components/CreditCardForm';
import PaymentInfo from '@zapier/payment/src/components/PaymentInfo';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';
import { useSize } from '@zapier/payment/src/hooks/useSize';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys,
  /** Currently select method */
  currentMethod?: PaymentSourceMethod,
  /** Error message to show */
  error?: string,
  /** Do not allow user to cancel card edit */
  forceCardUpdate: boolean,
  /** Hide switch component button */
  hideSwitch: boolean,
  /** Show editor */
  isEditing: boolean,
  /** Show loading */
  isEditingInFlight: boolean,
  /** Call to cancel edit */
  onCancelEdit: () => void,
  /** Call to start edit */
  onEdit: () => void,
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void,
  /** Call to save current edit */
  onSaveEdit: () => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod) => void,
  /** User's currently saved card details */
  savedSource: PaymentSource,
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const renderLabel = (
  props: Props,
  cardBrand: CreditCardBrandCode,
  isSmall: boolean
) => {
  return props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <React.Fragment>
      {isSmall && !props.hideSwitch ? null : (
        <PaymentMethodImage imageType={cardBrand} isSmall={true} />
      )}
      <Text tag="h3" type="subtitle700">
        {props.savedSource ? 'Edit Card' : 'Add Card'}
      </Text>
    </React.Fragment>
  );
};

const renderAside = (props: Props, isSmall: boolean) => {
  if (!props.isEditing) {
    return (
      <Button color="greyAlt" onClick={props.onEdit}>
        {props.savedSource ? `Edit${!isSmall ? ' Payment Method' : ''}` : 'Add'}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.supportedMethods}
      />
    );
  }
};

const PaymentMethodCard = (props: Props) => {
  const [cardBrand, setCardBrand] = useState(null);
  const size = useSize();
  const isSmall = size === 'small';
  // $FlowFixMe
  const cardDetails: PaymentDetailsCard = get(props, [
    'savedSource',
    'details',
  ]);
  const cardMethod = props.supportedMethods.find(
    m => m.method === paymentSourceMethods.card
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props, isSmall)}
      renderLabel={partial(renderLabel, props, cardBrand, isSmall)}
    >
      <CreditCardForm
        apiKeys={props.apiKeys}
        error={props.error}
        hasCancelButton={!props.forceCardUpdate}
        hasPaymentSource={Boolean(props.savedSource)}
        isInFlight={props.isEditingInFlight}
        onCancel={props.onCancelEdit}
        onCardNumberChange={setCardBrand}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(cardMethod, 'paymentProcessor')}
        savedCard={cardDetails}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodCard;
